import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ClientFields } from "@shared/enums/client-fields.enum";
import { ForOptTarif } from "@shared/enums/foropttarif.enum";
import { LessorType } from "@shared/enums/lessorType";
import { OptionalService } from "@shared/enums/optional-service.enum";
import { PackagesLabelType } from "@shared/enums/packages-label-type";
import { PolicyFieldModificationImpact } from "@shared/enums/policy-field-modification-impact.enum";
import { PolicyFields } from "@shared/enums/policy-fields.enum";
import { PhonePrefixes } from "@shared/models/phone-prefixes";
import { ChangeLogEntry, PolicyChangeLog } from "@shared/models/policy-change-log.model";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { DropdownModule } from "primeng/dropdown";
import { InputMaskModule } from "primeng/inputmask";
import { TableModule } from "primeng/table";
import { TabViewModule } from "primeng/tabview";

@Component({
  selector: "app-contract-changes",
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TranslateModule,
    TabViewModule,
    TableModule,
    CheckboxModule,
    InputMaskModule,
    DropdownModule,
    FormsModule,
  ],
  templateUrl: "./contract-changes.component.html",
  styles: [],
})
export class ContractChangesComponent implements OnChanges {
  @Input() policyChangeLog!: PolicyChangeLog;
  @Input() reference!: string;
  @Input() displaySignaturePhone!: boolean;
  @Input() phoneNumber!: string;
  @Input() phonePrefix!: PhonePrefixes;
  @Input() phonePrefixes!: PhonePrefixes[];
  @Input() multicontract!: boolean;
  @Output() signaturePhonePrefixChange = new EventEmitter<{ phonePrefix: PhonePrefixes; phoneMask: string }>();
  @Output() signaturePhoneNumberChange = new EventEmitter<string>();
  @Output() signaturePhoneSignatureCheckboxChange = new EventEmitter<boolean>();

  monoContractRecords!: Record<string, ChangeLogEntry[]>;
  multiContractRecords!: Record<string, ChangeLogEntry[]>;
  userChoiceRecords!: Record<string, ChangeLogEntry[]>;
  readonly defaultCountry = "France";
  readonly defaultPhonePattern = "6 10 20 30 40";
  readonly defaultPhoneMask = "9 99 99 99 99";
  readonly nombreEnfantsOldLabel = "Nombre d'enfants : x ";
  readonly nombreEnfantsNewLabel = "Nombre d'enfants :";
  readonly prenomEnfantOldLabel = "Prénom enfant X";
  readonly prenomEnfantNewLabel = "Prénom enfant";
  readonly nomEnfantOldLabel = "Nom enfant X";
  readonly nomEnfantNewLabel = "Nom enfant";
  readonly dateNaissanceEnfantOldLabel = "Date de naissance enfant X";
  readonly dateNaissanceEnfantNewLabel = "Date de naissance enfant";
  phoneMask: string = this.defaultPhoneMask;
  phonePattern: string = this.defaultPhonePattern;
  phoneSignatureCheckbox = false;

  constructor(private readonly translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["policyChangeLog"] && this.policyChangeLog) {
      this.setPhoneNumber();
      const monoContractRecords = this.generateRecordsByImpactAndCategory(
        this.policyChangeLog.changes,
        PolicyFieldModificationImpact.MonoContract
      );
      const multiContractRecords = this.generateRecordsByImpactAndCategory(
        this.policyChangeLog.changes,
        PolicyFieldModificationImpact.MultiContract
      );
      if (this.multicontract) {
        this.multiContractRecords = multiContractRecords;
        this.monoContractRecords = monoContractRecords;
      } else {
        this.monoContractRecords = { ...monoContractRecords, ...multiContractRecords };
        this.multiContractRecords = {};
      }
      this.userChoiceRecords = this.generateRecordsByImpactAndCategory(
        this.policyChangeLog.changes,
        PolicyFieldModificationImpact.UserChoice
      );
    }
  }

  hasValues(records: Record<string, ChangeLogEntry[]>): boolean {
    return Object.keys(records).length > 0;
  }

  setPhoneNumber() {
    const phonePrefixValue = this.policyChangeLog.changes.find(x => x.field.name === ClientFields.PhonePrefix);
    if (phonePrefixValue) {
      this.policyChangeLog.changes = this.policyChangeLog.changes.filter(entry => {
        return entry.field.name !== ClientFields.PhonePrefix && entry.field.name !== ClientFields.PhoneNumber;
      });
      const phoneNumberValue = this.policyChangeLog.changes.find(x => x.field.name === ClientFields.PhoneNumber);
      const phoneNumber = phoneNumberValue ? phoneNumberValue.newValue : this.phoneNumber;
      const newEntry = {
        field: {
          name: ClientFields.PhoneNumber,
          category: PolicyFields.CLIENT,
          modificationImpact: PolicyFieldModificationImpact.MultiContract,
        },
        oldValue: `${phonePrefixValue.oldValue} ${phoneNumber}`,
        newValue: `${phonePrefixValue.newValue} ${phoneNumber}`,
      } as ChangeLogEntry;
      this.policyChangeLog.changes.push(newEntry);
    } else {
      const phoneNumberValue = this.policyChangeLog.changes.find(x => x.field.name === ClientFields.PhoneNumber);
      if (phoneNumberValue) {
        phoneNumberValue.oldValue =
          phoneNumberValue.oldValue === "" ? "-" : `${this.phonePrefix.prefix} ${phoneNumberValue.oldValue}`;
        phoneNumberValue.newValue =
          phoneNumberValue.newValue === "" ? "-" : `${this.phonePrefix.prefix} ${phoneNumberValue.newValue}`;
      }
    }
  }

  getValue(category: string, field: string, value: string): string {
    if (value === "True") {
      return this.translate.instant("generalOptions.yes");
    }
    if (value === "False") {
      return this.translate.instant("generalOptions.no");
    }

    if (category === PolicyFields.FOROPTTARIF && field === ForOptTarif.PackageId) {
      return value === "1" ? PackagesLabelType.Confort : PackagesLabelType.ConfortPlus;
    }

    if (category === PolicyFields.FOROPTTARIF && field === ForOptTarif.DATE_ENFANT) {
      return this.formatDate(value);
    }

    if (category === PolicyFields.CLIENT && field === ClientFields.BirthDate) {
      return this.formatDate(value);
    }

    if (category === PolicyFields.FOROPTTARIF && field === ForOptTarif.OBJVAL && value === "") {
      return "5000";
    }

    if (category === PolicyFields.FOROPTTARIF && field === ForOptTarif.OBJVAL && value === "0") {
      return "-";
    }

    if (category === PolicyFields.CLIENT && field === ClientFields.PhoneNumber) {
      return value;
    }
    return value === "" || value === null ? "-" : value;
  }

  getLabel(field: string) {
    if (field === this.nombreEnfantsOldLabel) {
      return this.nombreEnfantsNewLabel;
    }
    if (field === this.prenomEnfantOldLabel) {
      return this.prenomEnfantNewLabel;
    }
    if (field === this.nomEnfantOldLabel) {
      return this.nomEnfantNewLabel;
    }
    if (field === this.dateNaissanceEnfantOldLabel) {
      return this.dateNaissanceEnfantNewLabel;
    }
    return field;
  }

  generateRecordsByImpactAndCategory(
    entries: ChangeLogEntry[],
    impact: PolicyFieldModificationImpact
  ): Record<string, ChangeLogEntry[]> {
    const containsLessorTypePrivate = entries.some(
      entry =>
        entry.field.category === PolicyFields.OPTIONALSERVICE &&
        entry.field.name === OptionalService.LessorType &&
        entry.newValue === LessorType.Private
    );

    return entries.reduce(
      (categoryRecords, entry) => {
        if (containsLessorTypePrivate && this.isUnwantedField(entry.field.name)) {
          return categoryRecords;
        }
        if (entry.field.modificationImpact === impact && !(entry.oldValue === null && entry.newValue === null)) {
          if (!categoryRecords[entry.field.category]) {
            categoryRecords[entry.field.category] = [];
          }
          categoryRecords[entry.field.category].push(entry);
        }
        return categoryRecords;
      },
      {} as Record<string, ChangeLogEntry[]>
    );
  }

  isUnwantedField(name: string) {
    return (
      name === OptionalService.LessorName ||
      name === OptionalService.AlreadyInHousing ||
      name === OptionalService.CustomerReference ||
      name === OptionalService.MainSiteNumber
    );
  }

  onPhonePrefixChange(event: PhonePrefixes): void {
    const additionalDigitsRequired = event.additionalDigitsRequired;
    this.updatePhoneFormat(additionalDigitsRequired);
    this.signaturePhonePrefixChange.emit({ phonePrefix: this.phonePrefix, phoneMask: this.phoneMask });
  }

  onPhoneNumberChange(): void {
    this.signaturePhoneNumberChange.emit(this.phoneNumber);
  }

  onPhoneSignatureCheckboxChange(checked: boolean): void {
    this.signaturePhoneSignatureCheckboxChange.emit(checked);
  }

  formatDate(timestamp: string): string {
    if (!timestamp) {
      return "";
    }
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;

    if (dateRegex.test(timestamp)) {
      return timestamp;
    }

    const date = new Date(timestamp);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }

  updatePhoneFormat(digitsCount: number) {
    switch (digitsCount) {
      case 6:
        this.phoneMask = "99 99 99";
        this.phonePattern = "75 10 20";
        break;
      case 8:
        this.phoneMask = "99 99 99 99";
        this.phonePattern = "87 10 20 30";
        break;
      case 9:
      default:
        this.phoneMask = this.defaultPhoneMask;
        this.phonePattern = this.phoneNumber ?? this.defaultPhonePattern;
    }
  }

  getTranslatedCategory(category: string): string {
    switch (category) {
      case PolicyFields.BANK:
        return this.translate.instant("additionalClausePolicyChangesDialog.bank");
      case PolicyFields.CLIENT:
        return this.translate.instant("additionalClausePolicyChangesDialog.client");
      case PolicyFields.DECLARATION:
        return this.translate.instant("additionalClausePolicyChangesDialog.declaration");
      case PolicyFields.DISASTER:
        return this.translate.instant("additionalClausePolicyChangesDialog.disaster");
      case PolicyFields.FOROPTTARIF:
        return this.translate.instant("additionalClausePolicyChangesDialog.forOptTarif");
      case PolicyFields.GENERAL:
        return this.translate.instant("additionalClausePolicyChangesDialog.general");
      case PolicyFields.OPTIONALSERVICE:
        return this.translate.instant("additionalClausePolicyChangesDialog.optionalServices");
      default:
        return category;
    }
  }

  readonly schoolInsuranceFieldName = "RCS";
  readonly childFieldsNames = ["PRENOM_ENFANT", "NOM_ENFANT", "DATE_ENFANT"];

  getSchoolInsuranceActivationFields(changes: ChangeLogEntry[]): ChangeLogEntry | undefined {
    const schoolInsurance = changes.find(change => change.field.name === this.schoolInsuranceFieldName);
    return schoolInsurance;
  }

  getSchoolInsuranceChildActivationFields(changes: ChangeLogEntry[]): ChangeLogEntry[] {
    return changes.filter(change => this.childFieldsNames.includes(change.field.name));
  }

  getGenericActivationFields(changes: ChangeLogEntry[]): ChangeLogEntry[] {
    return changes.filter(
      change => ![this.schoolInsuranceFieldName, ...this.childFieldsNames].includes(change.field.name)
    );
  }

  groupChildrenByIndex(changes: ChangeLogEntry[]): ChangeLogEntry[][] {
    if (!changes || changes.length === 0) return [];

    // Try to extract child index from field names (e.g. PRENOM_ENFANT_1)
    const childGroups: Record<number, ChangeLogEntry[]> = {};
    let maxChildIndex = 0;

    // First group by explicit index in field name
    changes.forEach(change => {
      const fieldName = change.field?.name || "";
      const match = fieldName.match(/(\d+)$/);

      // If we found an index in the field name
      if (match) {
        const childIndex = parseInt(match[1], 10) - 1; // Convert to 0-based
        if (!childGroups[childIndex]) {
          childGroups[childIndex] = [];
        }
        childGroups[childIndex].push(change);
        maxChildIndex = Math.max(maxChildIndex, childIndex);
      }
    });

    // For fields without explicit index, organize by field type
    const fieldsByType: Record<string, ChangeLogEntry[]> = {
      PRENOM: [],
      NOM: [],
      DATE: [],
    };

    // Group remaining fields by type
    changes.forEach(change => {
      const fieldName = change.field?.name || "";
      if (!fieldName.match(/(\d+)$/)) {
        if (fieldName.includes("PRENOM")) {
          fieldsByType["PRENOM"].push(change);
        } else if (fieldName.includes("NOM")) {
          fieldsByType["NOM"].push(change);
        } else if (fieldName.includes("DATE")) {
          fieldsByType["DATE"].push(change);
        }
      }
    });

    // Distribute fields without index to appropriate child groups
    ["PRENOM", "NOM", "DATE"].forEach(type => {
      fieldsByType[type].forEach((change, typeIndex) => {
        // If we have an existing group at this index, add the field to it
        if (childGroups[typeIndex]) {
          childGroups[typeIndex].push(change);
        }
        // Otherwise, create a new group
        else if (typeIndex <= maxChildIndex + fieldsByType[type].length) {
          childGroups[typeIndex] = [change];
          maxChildIndex = Math.max(maxChildIndex, typeIndex);
        }
      });
    });

    // Convert to array for template
    const result: ChangeLogEntry[][] = [];
    for (let i = 0; i <= maxChildIndex; i++) {
      if (childGroups[i] && childGroups[i].length > 0) {
        result.push(childGroups[i]);
      }
    }

    return result;
  }
}
